import { makeStyles } from "@mui/styles";
import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { Popover } from "components/Popover/Popover";
import RecordLabel from "components/RecordLabel";
import { resourceByName } from "config/resources";
import { FeResourceType } from "config/config";
import { MoveToLocationAutocomplete } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationAutocomplete";
import { moveToLocationAction } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationForm";
import { useMoveToLocation } from "containers/Resources/components/CommonActions/MoveToLocation/useMoveToLocation";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import { Resource } from "graphql/generated/graphql";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRelativeUrl } from "utils/service";
import { getRecordLocationId } from "utils/utils";
import "./MoveToLocation.scss";

interface MoveSingleResourceToLocationActionProps {
    resourceName: FeResourceType;
    resourceData: Resource;
    isDisabled?: boolean;
    iri?: string;
    afterAction?: () => void;
}

type MoveToSingleResourceToLocationProps = MoveSingleResourceToLocationActionProps & {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    iri?: string;
    afterAction?: () => void;
};

const LocationInfo = ({ data }: { data: Resource }) => {
    const { project_id_project, bundle_id_bundle, workspace_id_workspace } = data;
    // List queries do not carry the workspace icon; it lives in the store.
    const workspace = useSelector(getWorkspaceData);
    if (project_id_project || bundle_id_bundle || workspace_id_workspace) {
        const location = project_id_project ?? bundle_id_bundle ?? workspace_id_workspace;
        if (!location) {
            return null;
        }
        const locationName = project_id_project
            ? "project"
            : bundle_id_bundle
            ? "bundle"
            : "workspace";
        return (
            <RecordLabel
                link={getRelativeUrl(locationName, location.id)}
                name={location.name}
                image={location === workspace_id_workspace ? workspace?.icon : location.icon}
                icon={resourceByName[locationName].icon}
            />
        );
    } else {
        return null;
    }
};

export const MoveSingleResourceToLocationAction = ({
    resourceName,
    resourceData,
    isDisabled,
    iri,
    afterAction,
}: MoveSingleResourceToLocationActionProps) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <MoveSingleResourceToLocation
                isOpen={isOpen}
                resourceName={resourceName}
                resourceData={resourceData}
                iri={iri}
                afterAction={afterAction}
                onConfirm={() => {
                    setIsOpen(false);
                }}
                onCancel={() => {
                    setIsOpen(false);
                }}
            />
            <div className="locationInfoContainer">
                <LocationInfo data={resourceData} />
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    disabled={isDisabled}
                    icon={IconCollection.folderMoveOutlineIcon}
                />
            </div>
        </>
    );
};

const MoveSingleResourceToLocation = ({
    resourceName,
    resourceData,
    isOpen,
    onConfirm,
    onCancel,
    iri,
    afterAction,
}: MoveToSingleResourceToLocationProps) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const recordLocationId = getRecordLocationId(resourceData);

    const { selectedLocation, changeValue, options, isSameLocation } =
        useMoveToLocation(resourceName);

    const moveResource = () => {
        if (selectedLocation) {
            setIsLoading(true);
            moveToLocationAction({
                dispatch,
                resourceName,
                id: resourceData.id,
                selectedLocation,
                iri,
            })
                ?.then(() => afterAction?.())
                ?.then(() => onConfirm())
                ?.catch(console.error)
                ?.finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const rootStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    };
    const paperStyle = {
        maxHeight: "600px",
        overflow: "visible",
    };
    const useStyles = makeStyles(() => ({
        rootStyle,
        paperStyle,
    }));
    const classes = useStyles();

    useEffect(() => {
        if (!isOpen && selectedLocation) {
            changeValue("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
        <Popover
            isOpen={isOpen}
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="moveToLocation__popover"
            contentClassName="moveToLocation__content"
            anchorReference="none"
        >
            <div className="moveToLocation__header">Move selected resource to:</div>
            <MoveToLocationAutocomplete
                name="location"
                changeValue={changeValue}
                value={selectedLocation?.value}
                options={options}
                recordLocationId={recordLocationId}
            />

            <div className="button-container">
                <CancelButton onClick={onCancel} />
                <Button
                    className="confirm-button"
                    variation="primary"
                    disabled={!selectedLocation || isSameLocation}
                    loading={isLoading}
                    onClick={moveResource}
                    label={"Move"}
                />
            </div>
        </Popover>
    );
};
